import * as yup from "yup";
import validator from "validator";
import { InternalOptions } from "yup/lib/types";
import isNil from "lodash/isNil";
import isNull from "lodash/isNull";

import { formatPhoneNumber } from "utils/phoneNumberFormatter/phoneNumberFormatter";
import { PickUpVariant, PromoCode } from "types";

export type CreateReservationFormState = yup.InferType<
  typeof createReservationFormErrorSchema
>;

export const createReservationFormErrorSchema = yup.object({
  bookingContact: yup
    .object({
      firstName: yup.string().required("Please provide a valid First name"),
      lastName: yup.string().required("Please provide a valid Last name"),
      email: yup
        .string()
        .required("Please provide valid email address")
        .test("Email is valid", (value, { path, createError, options }) => {
          const { parent } = options as InternalOptions; // yup TS is off
          const email = parent.email;

          if (!validator.isEmail(email)) {
            return createError({
              path,
              message: "Please provide valid email address",
            });
          }

          return true;
        }),

      phoneFields: yup
        .object({
          mobilePhone: yup
            .string()
            .required("Please provide valid phone number")
            .test(
              "is possible phone number",
              (value, { path, createError, options }) => {
                const { parent } = options as InternalOptions; // yup TS is off

                const isPossible = formatPhoneNumber(
                  value,
                  parent.phoneCountryCode,
                  parent.phoneCountryDialCode
                )?.isPossible;

                if (!isPossible) {
                  return createError({
                    path,
                    message: "Please enter a valid phone number.",
                  });
                }

                return true;
              }
            ),
          phoneCountryCode: yup.string(),
          phoneCountryDialCode: yup.string(),
          phoneCountryFormat: yup.string(),
          phoneCountryName: yup.string(),
        })
        .required("Please provide valid phone number"),
    })
    .when("$isCreatingBookingContact", {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable(),
    }),

  driverGratuityPctCustomerInput: yup.mixed()
    .when(["$isGratuityEnabled"], {
      is: false,
      then: (schema) => schema.nullable(),
      otherwise: (schema) => {
        return schema
          .when(["$gratuityOptional"], {
            is: true,
            then: (schema) => {
              // Optional: can be null, but if provided must meet minimum (unless customGratuityNoMinimum is true)
              return schema
                .nullable()
                .test("minGratuityPct", (value, { createError, path, options }) => {
                  // Only validate if a non-cash value is provided and customGratuityNoMinimum is false
                  if (
                    value !== null && 
                    value !== "cash" && 
                    options.context.customGratuityEnabled && 
                    !options.context.customGratuityNoMinimum &&
                    value < options.context.minGratuityPct
                  ) {
                    return createError({
                      path,
                      message: `Gratuity must be at least ${options.context.minGratuityPct}%`,
                    });
                  }
                  return true;
                });
            },
            otherwise: (schema) => {
              // Required: must select and meet minimum (unless customGratuityNoMinimum is true)
              return schema.when({
                is: "cash",
                otherwise: (schema) => {
                  return schema
                    .required("You have to select a gratuity")
                    .test("minGratuityPct", (value, { createError, path, options }) => {
                      if (
                        !isNull(value) && 
                        options.context.customGratuityEnabled && 
                        !options.context.customGratuityNoMinimum &&
                        value < options.context.minGratuityPct
                      ) {
                        return createError({
                          path,
                          message: `Gratuity must be at least ${options.context.minGratuityPct}%`,
                        });
                      }
                      return true;
                    });
                },
              });
            },
          });
      },
    }),

  pickUpVariantSelected: yup
    .mixed<PickUpVariant>()
    .nullable()
    .when("$isPickUpVariantRequired", {
      is: true,
      then: (schema) => schema.required().oneOf(Object.values(PickUpVariant)),
      otherwise: (schema) => schema.notRequired(),
    }),

  // if payment new payment method block is not showing, this is null
  // temporarily removing stripe payment method id here
  paymentMethod: yup
    .object({
      creditCardError: yup
        .object()
        .nullable()
        .test("creditCardErrorExists", (value, { createError, path }) => {
          // we always error out, but in this pattern, the existance of any values
          // means we have a fail condition

          if (value?.message) {
            return createError({
              path,
              message: value.message,
            });
          }

          return true;
        }),
      cardholderName: yup
        .string()
        .nullable()
        .when("$isPaymentElement", {
          is: true,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) =>
            schema.when("creditCardError", {
              is: (value) => isNil(value),
              otherwise: (schema) =>
                schema.required("Cardholder name is required"),
            }),
        }),
    })
    .when("$isNewPaymentMethodRequired", {
      is: false,
      then: (schema) => schema.nullable(),
    }),

  isTermsAccepted: yup.boolean().when("$isTermsRequired", {
    is: true,
    then: (schema) =>
      schema
        .required("Please accept the Terms & Conditions")
        .oneOf([true], "The terms and conditions must be accepted."),
  }),

  // an existing payment method, only used when authenticated
  stripePaymentMethodId: yup
    .string()
    .nullable()
    .when("$isExistingPaymentMethodRequired", {
      is: true,
      then: (schema) => schema.required("Please select a payment card."),
    }),

  printedName: yup.string().when("$isConsentSignatureRequired", {
    is: true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.nullable(),
  }),

  autoPaymentAmt: yup.number().nullable(),

  autoPaymentType: yup.string().nullable(),

  promoCodeCustomerInput: yup.mixed<PromoCode>().nullable(),

  additonalItemsByTrip: yup.array(
    yup.object({
      childSeats: yup.object({
        boosterSeatQuantity: yup.number().nullable(),
        boosterSeatAmt: yup.number().nullable(),
        forwardFacingSeatQuantity: yup.number().nullable(),
        forwardFacingSeatAmt: yup.number().nullable(),
        rearFacingSeatQuantity: yup.number().nullable(),
        rearFacingSeatAmt: yup.number().nullable(),
      }),
    })
  ),

  route: yup
    .object({
      carryOnLuggage: yup.number().min(0).default(0),
      checkedLuggage: yup.number().min(0).default(0),
      oversizeLuggage: yup.number().min(0).default(0),
    })
    .default({ carryOnLuggage: 0, checkedLuggage: 0, oversizeLuggage: 0 }),
});
